import React, { useState } from "react";
import { ColDef, ColGroupDef } from "ag-grid-community";
import ReportPageShell from "../components/ReportPageShell";
import DetailTable from "../components/DetailTable";
import { defaultFilters, toQueryParams } from "../shared/filters";
import { useDetailQuery } from "../shared/useDetailQuery";
import { useSearchGate } from "../shared/useSearchGate";
import {
  inclusionCol,
  intCol,
  moneyCol,
  textCol,
} from "../shared/detailColumns";

const LIMIT_OPTIONS = [1000, 5000, 20000, 100000];
const DEFAULT_LIMIT = 5000;

const TOOLTIP =
  "Order-item detail (RP-2165) — one row per line item on the settled sales in scope, for inventory / " +
  "balancing. Line subtotal/tax/discount plus the applied modifiers (Square-style). Scope by " +
  "event/date/vendor/location, then export.";

const CSV_LEGEND = [
  "",
  "One row per order-item line on settled sales in the window/scope.",
  "Units / Item Revenue (counted) = this row's contribution to the item totals.",
  "Modifiers / Modifier $ / Modifier Summary = applied modifiers on the line (sparse until the modifier backfill runs).",
];

function orderItemColumns(): (ColDef | ColGroupDef)[] {
  return [
    inclusionCol(),
    textCol("businessDay", "Business Day", { pinned: "left", width: 120 }),
    textCol("name", "Item", { pinned: "left", width: 200 }),
    textCol("itemId", "Item Id", { width: 150, valueFormatter: (p) => (p.value as string) ?? "" }),
    textCol("orderId", "Order Id", { width: 150 }),
    textCol("eventId", "Event Id", { width: 150 }),
    textCol("vendorId", "Vendor Id", { width: 150 }),
    textCol("orderStatus", "Order Status", { width: 120 }),
    intCol("quantity", "Qty"),
    moneyCol("unitPrice", "Unit Price"),
    moneyCol("lineSubtotal", "Line Subtotal"),
    moneyCol("lineTax", "Line Tax"),
    moneyCol("lineDiscount", "Line Discount"),
    // Modifiers — present once the reporting-backlog-assess engine deploys (sparse until backfill).
    intCol("modifierCount", "Modifiers"),
    moneyCol("modifierTotal", "Modifier $"),
    textCol("modifierSummary", "Modifier Summary", {
      width: 240,
      valueFormatter: (p) => (p.value as string) ?? "",
    }),
    intCol("unitsContribution", "Units"),
    moneyCol("itemRevenueContribution", "Item Revenue (counted)"),
    textCol("inclusionReason", "Inclusion Reason", { width: 260 }),
  ];
}

// Order-item detail page (RP-2165) — OrderItemsList (inventory / balancing + modifiers).
export default function OrderItemsReport() {
  const [filters, setFilters] = useState(defaultFilters);
  const [limit, setLimit] = useState<number>(DEFAULT_LIMIT);
  const { submitted, isDirty, hasRun, search } = useSearchGate({ filters, limit });
  const state = useDetailQuery(
    submitted
      ? toQueryParams("OrderItemsList", submitted.filters, { limit: submitted.limit })
      : null
  );

  return (
    <ReportPageShell
      title="Order Items"
      tooltipText={TOOLTIP}
      filters={filters}
      onFiltersChange={setFilters}
      state={state}
      onSearch={search}
      isDirty={isDirty}
      hasRun={hasRun}
      filterVendor
      filterLocation
      actions={
        <label className="flex items-center gap-1 text-sm text-gray-600">
          Row limit
          <select
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            className="rounded-md border border-gray-300 px-2 py-1 text-sm"
          >
            {LIMIT_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n.toLocaleString()}
              </option>
            ))}
          </select>
        </label>
      }
    >
      {(data) => (
        <DetailTable
          data={data}
          columnDefs={orderItemColumns()}
          summaryMetrics={["Units", "ItemRevenue", "ItemTax", "ItemDiscount"]}
          emphasisMetric="ItemRevenue"
          exportName="OrderItems"
          csvExtra={CSV_LEGEND}
        />
      )}
    </ReportPageShell>
  );
}
